<template>
  <div class="developer-dashboard py-4">
    <div class="container-fluid">
      <!-- Welcome Header -->
      <div class="welcome-banner mb-4 p-4 text-left shadow-lg rounded-xl text-white">
        <div class="row align-items-center">
          <div class="col-md-8">
            <h2 class="font-weight-bold mb-1">Developer Control Tower</h2>
            <p class="m-0 text-white-50">Benvenuto nello spazio sviluppatore di Campania Sistemi. Monitora lo stato della piattaforma roadME, gestisci i tenant e configura i moduli di sistema.</p>
          </div>
          <div class="col-md-4 text-md-right mt-3 mt-md-0">
            <div class="system-status-indicator d-inline-flex align-items-center bg-white-10 px-3 py-2 rounded-pill">
              <span class="status-dot online mr-2"></span>
              <span class="small font-weight-bold text-uppercase tracking-wider">Sistema Online</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Quick Stats Grid -->
      <div class="row mb-4">
        <!-- Stat 1: Aziende -->
        <div class="col-lg-3 col-sm-6 mb-3">
          <div class="developer-stat-card cw-card p-0 h-100 text-left overflow-hidden">
            <div class="card-body p-4 d-flex align-items-center justify-content-between">
              <div>
                <span class="d-block text-muted small font-weight-semibold text-uppercase mb-1">Aziende Clienti</span>
                <h3 class="m-0 font-weight-bold text-dark">{{ companies.length }}</h3>
                <router-link :to="{ name: 'admin-companies' }" class="text-primary small font-weight-bold mt-2 d-inline-block">
                  Gestisci <i class="ti-arrow-right ml-1"></i>
                </router-link>
              </div>
              <div class="stat-icon-wrapper bg-soft-primary text-primary">
                <i class="ti-building"></i>
              </div>
            </div>
            <div class="stat-card-progress bg-primary"></div>
          </div>
        </div>

        <!-- Stat 2: Moduli Attivi -->
        <div class="col-lg-3 col-sm-6 mb-3">
          <div class="developer-stat-card cw-card p-0 h-100 text-left overflow-hidden">
            <div class="card-body p-4 d-flex align-items-center justify-content-between">
              <div>
                <span class="d-block text-muted small font-weight-semibold text-uppercase mb-1">Moduli di Sistema</span>
                <h3 class="m-0 font-weight-bold text-dark">{{ allModules.length }}</h3>
                <router-link :to="{ name: 'admin-companies' }" class="text-success small font-weight-bold mt-2 d-inline-block">
                  Configura <i class="ti-arrow-right ml-1"></i>
                </router-link>
              </div>
              <div class="stat-icon-wrapper bg-soft-success text-success">
                <i class="ti-layers-alt"></i>
              </div>
            </div>
            <div class="stat-card-progress bg-success"></div>
          </div>
        </div>

        <!-- Stat 3: Utenti Totali -->
        <div class="col-lg-3 col-sm-6 mb-3">
          <div class="developer-stat-card cw-card p-0 h-100 text-left overflow-hidden">
            <div class="card-body p-4 d-flex align-items-center justify-content-between">
              <div>
                <span class="d-block text-muted small font-weight-semibold text-uppercase mb-1">Utenti Registrati</span>
                <h3 class="m-0 font-weight-bold text-dark">{{ users.length }}</h3>
                <router-link :to="{ name: 'admin-users' }" class="text-warning small font-weight-bold mt-2 d-inline-block">
                  Gestisci <i class="ti-arrow-right ml-1"></i>
                </router-link>
              </div>
              <div class="stat-icon-wrapper bg-soft-warning text-warning">
                <i class="ti-user"></i>
              </div>
            </div>
            <div class="stat-card-progress bg-warning"></div>
          </div>
        </div>

        <!-- Stat 4: Cataloghi Intervento -->
        <div class="col-lg-3 col-sm-6 mb-3">
          <div class="developer-stat-card cw-card p-0 h-100 text-left overflow-hidden">
            <div class="card-body p-4 d-flex align-items-center justify-content-between">
              <div>
                <span class="d-block text-muted small font-weight-semibold text-uppercase mb-1">Tipologie Catalogo</span>
                <h3 class="m-0 font-weight-bold text-dark">{{ tipologie.length }}</h3>
                <router-link :to="{ name: 'admin-interventi' }" class="text-danger small font-weight-bold mt-2 d-inline-block">
                  Vedi Catalogo <i class="ti-arrow-right ml-1"></i>
                </router-link>
              </div>
              <div class="stat-icon-wrapper bg-soft-danger text-danger">
                <i class="ti-package"></i>
              </div>
            </div>
            <div class="stat-card-progress bg-danger"></div>
          </div>
        </div>
      </div>

      <div class="row">
        <!-- Left Column: Infrastructure Health & Quick Actions -->
        <div class="col-xl-4 mb-4 text-left">
          <!-- Infrastructure Health Card -->
          <div class="cw-card mb-4">
            <div class="card-body p-0">
              <h5 class="font-weight-bold text-dark text-uppercase mb-3 d-flex align-items-center">
                <i class="ti-pulse mr-2 text-primary"></i> Stato Infrastruttura
              </h5>
              
              <div class="health-services-list">
                <!-- Service 1: API Server -->
                <div class="health-service-item d-flex justify-content-between align-items-center py-2 border-bottom">
                  <div>
                    <span class="font-weight-semibold text-dark d-block">Server API roadME</span>
                    <small class="text-muted">Latenza: <span class="text-success font-weight-bold">{{ apiLatency }}ms</span></small>
                  </div>
                  <span class="badge badge-soft-success px-3 py-1 rounded-pill font-weight-bold">
                    <span class="pulse-dot mr-1 bg-success"></span> Attivo
                  </span>
                </div>

                <!-- Service 2: Database -->
                <div class="health-service-item d-flex justify-content-between align-items-center py-2 border-bottom">
                  <div>
                    <span class="font-weight-semibold text-dark d-block">Database Server</span>
                    <small class="text-muted">Connessioni: <span class="font-weight-bold">12 attive</span></small>
                  </div>
                  <span class="badge badge-soft-success px-3 py-1 rounded-pill font-weight-bold">
                    <span class="pulse-dot mr-1 bg-success"></span> Online
                  </span>
                </div>

                <!-- Service 3: Push Notifications -->
                <div class="health-service-item d-flex justify-content-between align-items-center py-2 border-bottom">
                  <div>
                    <span class="font-weight-semibold text-dark d-block">FCM Push Gateway</span>
                    <small class="text-muted">Firebase Cloud Messaging</small>
                  </div>
                  <span class="badge badge-soft-success px-3 py-1 rounded-pill font-weight-bold">
                    <span class="pulse-dot mr-1 bg-success"></span> Online
                  </span>
                </div>

                <!-- Service 4: GeoServer & Map Tiles -->
                <div class="health-service-item d-flex justify-content-between align-items-center py-2">
                  <div>
                    <span class="font-weight-semibold text-dark d-block">Server Mappe (Leaflet)</span>
                    <small class="text-muted">Tile cache hit: <span class="font-weight-bold">98.4%</span></small>
                  </div>
                  <span class="badge badge-soft-success px-3 py-1 rounded-pill font-weight-bold">
                    <span class="pulse-dot mr-1 bg-success"></span> Online
                  </span>
                </div>
              </div>

              <!-- Manual Ping Button -->
              <button 
                class="btn btn-outline-primary btn-block btn-sm rounded-pill mt-3 font-weight-semibold" 
                @click="pingApi" 
                :disabled="pinging"
              >
                <i class="ti-reload mr-1" :class="{'rotate-icon': pinging}"></i>
                {{ pinging ? 'Verifica latenza...' : 'Test Latenza API' }}
              </button>
            </div>
          </div>

          <!-- Quick Admin Actions -->
          <div class="cw-card">
            <div class="card-body p-0">
              <h5 class="font-weight-bold text-dark text-uppercase mb-3 d-flex align-items-center">
                <i class="ti-settings mr-2 text-primary"></i> Pannello Sviluppo
              </h5>
              
              <div class="quick-actions-grid">
                <router-link :to="{ name: 'admin-companies' }" class="quick-action-btn">
                  <div class="action-icon bg-soft-primary text-primary">
                    <i class="ti-building"></i>
                  </div>
                  <span class="action-label">Gestione Aziende</span>
                </router-link>

                <router-link :to="{ name: 'admin-interventi' }" class="quick-action-btn">
                  <div class="action-icon bg-soft-success text-success">
                    <i class="ti-layers-alt"></i>
                  </div>
                  <span class="action-label">Catalogo Tipologie</span>
                </router-link>

                <router-link :to="{ name: 'admin-team-types' }" class="quick-action-btn">
                  <div class="action-icon bg-soft-secondary text-secondary">
                    <i class="ti-bookmark-alt"></i>
                  </div>
                  <span class="action-label">Tipologie Squadra</span>
                </router-link>

                <router-link :to="{ name: 'admin-users' }" class="quick-action-btn">
                  <div class="action-icon bg-soft-warning text-warning">
                    <i class="mdi mdi-domain"></i>
                  </div>
                  <span class="action-label">Gestione Utenti</span>
                </router-link>

                <router-link :to="{ name: 'realtime-map' }" class="quick-action-btn">
                  <div class="action-icon bg-soft-info text-info">
                    <i class="ti-map"></i>
                  </div>
                  <span class="action-label">Mappa Realtime</span>
                </router-link>
              </div>

              <!-- Quick Demo Tools -->
              <div class="mt-4 pt-3 border-top">
                <p class="small font-weight-bold text-muted text-uppercase mb-2">Simulazione / Strumenti</p>
                <div class="form-group mb-2">
                  <select v-model="selectedTenantFilter" class="form-control form-control-sm rounded-pill px-3">
                    <option :value="null">Filtra per Azienda (Nessuno)</option>
                    <option v-for="c in companies" :key="c.id" :value="c.id">{{ c.nome }}</option>
                  </select>
                </div>
                <button 
                  class="btn btn-outline-secondary btn-sm btn-block rounded-pill font-weight-bold" 
                  @click="generateSimulatedLog"
                >
                  <i class="ti-bolt mr-1"></i> Genera Log Attività
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column: Tenant Monitoring & Logs -->
        <div class="col-xl-8 mb-4 text-left">
          <!-- Tenants Table -->
          <div class="cw-card mb-4">
            <div class="card-body p-0">
              <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap">
                <h5 class="font-weight-bold text-dark text-uppercase m-0 d-flex align-items-center">
                  <i class="ti-layout-grid3 mr-2 text-primary"></i> Monitoraggio Tenant Attivi
                </h5>
                <router-link :to="{ name: 'admin-companies' }" class="btn btn-sm btn-outline-primary rounded-pill px-3 font-weight-semibold">
                  Aggiungi Azienda
                </router-link>
              </div>

              <!-- Table -->
              <div class="table-responsive" style="max-height: 350px;">
                <table class="au-table">
                  <thead>
                    <tr>
                      <th class="border-0 text-muted font-weight-bold small">ID</th>
                      <th class="border-0 text-muted font-weight-bold small">Azienda</th>
                      <th class="border-0 text-muted font-weight-bold small">P. IVA</th>
                      <th class="border-0 text-muted font-weight-bold small">Moduli Abilitati</th>
                      <th class="border-0 text-muted font-weight-bold small text-right">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="loadingData">
                      <td colspan="5" class="text-center py-4">
                        <span class="spinner-border spinner-border-sm text-primary mr-2" role="status"></span>
                        Caricamento tenant...
                      </td>
                    </tr>
                    <tr v-else-if="companies.length === 0">
                      <td colspan="5" class="text-center py-4 text-muted">
                        Nessun tenant configurato.
                      </td>
                    </tr>
                    <tr v-else v-for="company in filteredCompanies" :key="company.id">
                      <td class="align-middle font-weight-bold text-muted">{{ company.id }}</td>
                      <td class="align-middle">
                        <div class="d-flex align-items-center">
                          <div class="company-avatar mr-2 bg-gradient-purple">{{ company.nome.charAt(0).toUpperCase() }}</div>
                          <span class="font-weight-semibold text-dark">{{ company.nome }}</span>
                        </div>
                      </td>
                      <td class="align-middle">
                        <code class="text-secondary small font-weight-bold">{{ company.partita_iva || '—' }}</code>
                      </td>
                      <td class="align-middle">
                        <div class="modules-badge-container">
                          <span 
                            v-for="mod in (companyModules[company.id] || [])" 
                            :key="mod.id" 
                            class="badge badge-soft-indigo mr-1 mb-1"
                          >
                            <i :class="mod.icona || 'ti-layers'" class="mr-1"></i>{{ mod.nome }}
                          </span>
                          <span v-if="!(companyModules[company.id])" class="text-muted small">Caricamento...</span>
                          <span v-else-if="companyModules[company.id].length === 0" class="text-muted small font-italic">Nessuno</span>
                        </div>
                      </td>
                      <td class="align-middle text-right">
                        <router-link 
                          :to="{ name: 'admin-companies' }" 
                          class="btn btn-sm btn-outline-primary rounded-pill px-3 font-weight-semibold"
                        >
                          Configura
                        </router-link>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Developer Audit / Log Terminal -->
          <div class="cw-card">
            <div class="card-body p-4 bg-dark-slate rounded-lg text-light">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h5 class="font-weight-bold text-white text-uppercase m-0 d-flex align-items-center">
                  <i class="ti-receipt mr-2 text-success"></i> Console Attività roadME (Live Feed)
                </h5>
                <button class="btn btn-xs btn-outline-light rounded-pill px-3 font-weight-bold" style="font-size: 0.7rem;" @click="clearLogs">
                  Pulisci Console
                </button>
              </div>

              <!-- Terminal Window -->
              <div class="terminal-window p-3 rounded-lg bg-black-20 font-family-monospace text-left" ref="terminal">
                <div 
                  v-for="(log, idx) in filteredLogs" 
                  :key="idx" 
                  class="terminal-line mb-2 d-flex align-items-start"
                >
                  <span class="text-muted small mr-2">[{{ log.time }}]</span>
                  <span class="badge px-2 py-0 mr-2" :class="getLogBadgeClass(log.level)">
                    {{ log.level.toUpperCase() }}
                  </span>
                  <span class="flex-grow-1" :class="getLogTextClass(log.level)">
                    <strong v-if="log.tenant">[{{ log.tenant }}]</strong> {{ log.message }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "DeveloperDashboard",
  data() {
    return {
      companies: [],
      allModules: [],
      users: [],
      tipologie: [],
      companyModules: {},
      loadingData: false,
      
      // Live ping
      pinging: false,
      apiLatency: 42,
      
      // Logs
      selectedTenantFilter: null,
      logs: [
        { time: "16:20:05", level: "info", message: "Connessione stabilita con il server API.", tenant: null },
        { time: "16:20:07", level: "success", message: "Inizializzazione moduli completata.", tenant: null },
        { time: "16:20:12", level: "info", message: "Recupero della lista degli utenti globali completato con successo.", tenant: null },
        { time: "16:21:30", level: "success", message: "Autenticazione sviluppatore verificata per Campania Sistemi srl.", tenant: "CSI" },
        { time: "16:22:45", level: "info", message: "Avvio sincronizzazione automatica tenant.", tenant: null }
      ],
      
      // Simulation interval
      simInterval: null,
    };
  },

  computed: {
    filteredCompanies() {
      if (!this.selectedTenantFilter) return this.companies;
      return this.companies.filter(c => c.id === this.selectedTenantFilter);
    },

    filteredLogs() {
      if (!this.selectedTenantFilter) return this.logs;
      const comp = this.companies.find(c => c.id === this.selectedTenantFilter);
      if (!comp) return this.logs;
      return this.logs.filter(log => !log.tenant || log.tenant === comp.nome || log.tenant === 'CSI');
    }
  },

  mounted() {
    this.fetchData();
    this.startSimulation();
    
    // Scroll terminal to bottom
    this.$nextTick(() => {
      this.scrollToBottom();
    });
  },

  beforeDestroy() {
    if (this.simInterval) {
      clearInterval(this.simInterval);
    }
  },

  methods: {
    async fetchData() {
      this.loadingData = true;
      try {
        const [companiesRes, modulesRes, usersRes, tipologieRes] = await Promise.all([
          this.axios.get("/companies"),
          this.axios.get("/modules"),
          this.axios.get("/get-users"),
          this.axios.get("/tipologie-intervento")
        ]);

        this.companies = Array.isArray(companiesRes.data) ? companiesRes.data : [];
        this.allModules = Array.isArray(modulesRes.data) ? modulesRes.data : [];
        this.users = Array.isArray(usersRes.data) ? usersRes.data : [];
        this.tipologie = Array.isArray(tipologieRes.data) ? tipologieRes.data : [];

        // Load modules for each company
        const promises = this.companies.map(async (c) => {
          try {
            const res = await this.axios.get(`/companies/${c.id}/modules`);
            this.$set(this.companyModules, c.id, Array.isArray(res.data) ? res.data : []);
          } catch (e) {
            this.$set(this.companyModules, c.id, []);
          }
        });
        await Promise.all(promises);

        this.addLog("success", "Sincronizzazione dati tenant completata.");
      } catch (err) {
        console.error("Errore caricamento dati dashboard:", err);
        this.addLog("error", "Errore durante il recupero dei dati del server.");
      } finally {
        this.loadingData = false;
      }
    },

    async pingApi() {
      this.pinging = true;
      const start = Date.now();
      try {
        await this.axios.get("/modules");
        this.apiLatency = Date.now() - start;
        this.addLog("info", `Ping server API roadME: ${this.apiLatency}ms`);
      } catch (e) {
        this.apiLatency = "ERR";
        this.addLog("error", "Tentativo di ping fallito. Server API non raggiungibile.");
      } finally {
        this.pinging = false;
      }
    },

    addLog(level, message, tenant = null) {
      const now = new Date();
      const timeStr = now.toTimeString().split(" ")[0];
      this.logs.push({
        time: timeStr,
        level,
        message,
        tenant
      });
      // Cap log size at 50
      if (this.logs.length > 50) {
        this.logs.shift();
      }
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },

    clearLogs() {
      this.logs = [];
    },

    scrollToBottom() {
      const term = this.$refs.terminal;
      if (term) {
        term.scrollTop = term.scrollHeight;
      }
    },

    getLogBadgeClass(level) {
      return {
        "badge-soft-success": level === "success",
        "badge-soft-info": level === "info",
        "badge-soft-warning": level === "warning",
        "badge-soft-danger": level === "error"
      };
    },

    getLogTextClass(level) {
      return {
        "text-success": level === "success",
        "text-info": level === "info",
        "text-warning": level === "warning",
        "text-danger": level === "error",
        "text-light": level === "default"
      };
    },

    generateSimulatedLog() {
      if (this.companies.length === 0) return;
      const randomCompany = this.companies[Math.floor(Math.random() * this.companies.length)];
      
      const logTemplates = [
        { level: "info", msg: "Verificato token sessione operatore." },
        { level: "success", msg: "Notifica push di avvio cantiere recapitata con successo." },
        { level: "info", msg: "Recupero coordinate GPS segnalazioni." },
        { level: "warning", msg: "Segnalazione #129 contrassegnata come emergenza." },
        { level: "success", msg: "Lavoro #40 completato e archiviato." },
        { level: "info", msg: "Esportazione report PDF giornaliero." }
      ];

      const template = logTemplates[Math.floor(Math.random() * logTemplates.length)];
      this.addLog(template.level, template.msg, randomCompany.nome);
    },

    startSimulation() {
      // Simulate activity every 15-30 seconds
      const runSim = () => {
        this.generateSimulatedLog();
        const nextTime = Math.random() * 15000 + 15000;
        this.simInterval = setTimeout(runSim, nextTime);
      };
      this.simInterval = setTimeout(runSim, 20000);
    }
  }
};
</script>

<style scoped>
.developer-dashboard {
  background-color: #f4f6f9;
  min-height: 100vh;
}

.welcome-banner {
  background: linear-gradient(135deg, #1e293b, #0f172a);
  border-radius: 16px;
  position: relative;
  overflow: hidden;
}

.welcome-banner::before {
  content: "";
  position: absolute;
  top: -50%;
  right: -20%;
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, rgba(247, 147, 30, 0.15) 0%, transparent 60%);
  border-radius: 50%;
}

.bg-white-10 {
  background-color: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.status-dot.online {
  background-color: #10b981;
  box-shadow: 0 0 8px #10b981;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { transform: scale(0.9); opacity: 1; }
  50% { transform: scale(1.2); opacity: 0.6; }
  100% { transform: scale(0.9); opacity: 1; }
}

.rounded-xl {
  border-radius: 16px;
}

.rounded-lg {
  border-radius: 12px;
}

/* Stat Cards */
.developer-stat-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}

.developer-stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.08) !important;
}

.stat-icon-wrapper {
  width: 52px;
  height: 52px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}

.bg-soft-primary { background-color: rgba(79, 70, 229, 0.1); }
.bg-soft-success { background-color: rgba(16, 185, 129, 0.1); }
.bg-soft-warning { background-color: rgba(245, 158, 11, 0.1); }
.bg-soft-danger { background-color: rgba(239, 68, 68, 0.1); }

.stat-card-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  width: 100%;
}

/* Infrastructure Health */
.pulse-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  animation: pulse 1.5s infinite;
}

.badge-soft-success {
  background-color: rgba(16, 185, 129, 0.1);
  color: #059669;
}

.badge-soft-info {
  background-color: rgba(6, 182, 212, 0.1);
  color: #0891b2;
}

.badge-soft-indigo {
  background-color: rgba(247, 147, 30, 0.1);
  color: #F7931E;
  border: 1px solid rgba(247, 147, 30, 0.2);
}

.badge-soft-warning {
  background-color: rgba(245, 158, 11, 0.1);
  color: #d97706;
}

.badge-soft-danger {
  background-color: rgba(239, 68, 68, 0.1);
  color: #dc2626;
}

/* Quick Actions */
.quick-actions-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.quick-action-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 8px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background-color: #fff;
  transition: all 0.2s;
  text-decoration: none !important;
}

.quick-action-btn:hover {
  border-color: #6366f1;
  box-shadow: 0 4px 12px rgba(247, 147, 30, 0.08);
  transform: translateY(-2px);
}

.quick-action-btn .action-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  margin-bottom: 8px;
}

.quick-action-btn .action-label {
  font-size: 0.75rem;
  font-weight: 700;
  color: #475569;
}

/* Company Table */
.company-avatar {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  color: #fff;
  font-weight: 700;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.bg-gradient-purple {
  background: linear-gradient(135deg, #6366f1, #a855f7);
}

/* Terminal Log Console */
.bg-dark-slate {
  background-color: #1e293b;
}

.bg-black-20 {
  background-color: rgba(0, 0, 0, 0.3);
}

.terminal-window {
  height: 250px;
  overflow-y: auto;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.font-family-monospace {
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 0.8rem;
  line-height: 1.5;
}

.terminal-line {
  border-bottom: 1px solid rgba(255, 255, 255, 0.02);
  padding-bottom: 4px;
}

.rotate-icon {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.btn-xs {
  padding: 2px 8px;
  font-size: 0.7rem;
}
</style>
